import React, { useState } from "react";
import { redeemInviteCode } from "../../services/onboardingService";

interface CoachCodeCardProps {
  /** Called after a code links the athlete to a coaching group. */
  onRedeemed?: () => void;
}

/** Enter the code from a coach's invite email to join their coaching group. */
const CoachCodeCard: React.FC<CoachCodeCardProps> = ({ onRedeemed }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = code.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const { coachName } = await redeemInviteCode(trimmed);
      setSuccess(coachName ? `You're now connected with ${coachName}.` : "You're now connected with your coach.");
      setCode("");
      onRedeemed?.();
    } catch (err) {
      const raw = err instanceof Error ? err.message.replace(/^Firebase:\s*/i, "").trim() : "";
      setError(raw || "That code doesn't look right. Double check it and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form id="coach-code" onSubmit={submit} className="mt-6 rounded-lg border border-base-300 bg-base-100 p-6">
      <h2 className="text-lg font-semibold text-base-content">Coach code</h2>
      <p className="mt-1 text-sm text-base-content/70">
        Got an invite code from your coach or team? Enter it here to join their coaching group.
      </p>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <input
          type="text"
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            setError(null);
          }}
          placeholder="PEAK-XXXX"
          aria-label="Coach code"
          autoCapitalize="characters"
          autoComplete="off"
          className="input input-bordered w-full flex-1 uppercase"
        />
        <button type="submit" className="btn btn-primary sm:w-36" disabled={busy || !code.trim()}>
          {busy ? <span className="loading loading-spinner loading-sm" /> : "Apply code"}
        </button>
      </div>
      {error && <p className="mt-3 text-sm text-error" role="alert">{error}</p>}
      {success && <p className="mt-3 text-sm text-success" role="status">{success}</p>}
    </form>
  );
};

export default CoachCodeCard;
